<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable
      ref="rtable"
      :columns="columnDefs"
      :settings="{ theme: 'ag-theme-alpine', title: t('expenses') }"
      endpoint="reports/accounting/expenses"
      @onDataLoaded="onDataLoaded"
    >
      <template #filter>
        <el-select
          filterable
          clearable
          class="col-span-12 md:col-span-3 capitalize"
          :placeholder="t('selectType')"
          v-model="selectedType"
          @change="onSelectedTypeChanged"
        >
          <el-option
            v-for="(option, index) in ['company', 'hr']"
            class="capitalize"
            :key="index"
            :label="t(option)"
            :value="option"
          ></el-option>
        </el-select>
      </template>
      <template #cards>
        <div class="grid grid-cols-3 gap-4">
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('totalExpenses') }}</div>
              <div class="text-2xl pt-2">{{ formatMoney(totalCompanyExpenses + totalHRExpenses, 'IQD') }}</div>
            </div>
          </el-card>
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('company') }}</div>
              <div class="text-2xl pt-2">{{ formatMoney(totalCompanyExpenses, 'IQD') }}</div>
            </div>
          </el-card>
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('hr') }}</div>
              <div class="text-2xl pt-2">{{ formatMoney(totalHRExpenses, 'IQD') }}</div>
            </div>
          </el-card>
        </div>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import { ColDef } from 'ag-grid-community'
import { useRoute, useRouter } from 'vue-router'
import { useHTTP } from '@/plugins/http'
import { useAlertModal } from '@/plugins/alert-modal/alert-modal'
import { formatMoney } from '@/utils/money'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import { formatPostgresDate } from '@/utils/date'
import { useContext } from '@/plugins/context'

// i18n
const { t } = useI18n({
  messages: {
    en: {
      expenses: 'Expenses',
      amount: 'Amount',
      type: 'Type',
      reason: 'What For',
      payee: 'Payee Name',
      inputDate: 'Input Date',
      time: 'Input Time',
      selectType: 'Select type (Company / HR)',
      company: 'Company',
      hr: 'HR',
      totalExpenses: 'Total Expenses',
      salary: 'Salary',
      bonus: 'Bonus',
      commission: 'Commission',
      fees: 'Fees',
    },
    ar: {
      expenses: 'المصاريف',
      amount: 'المبلغ',
      type: 'النوع',
      reason: 'صرف على',
      payee: 'المستفيد',
      date: 'التاريخ',
      inputDate: 'تاريخ الانشاء',
      time: 'الوقت',
      selectType: 'اختر النوع (شركة / موارد بشرية)',
      company: 'شركة',
      hr: 'موارد بشرية',
      totalExpenses: 'اجمالي المصاريف',
      salary: 'راتب',
      bonus: 'مكافئة',
      commission: 'عمولة',
      fees: 'اجور',
    }
  }
})

const ctx = useContext()

// Data
const rtable = ref<any>()
const columnDefs = ref<ColDef[]>([])

const selectedType = ref()

const totalCompanyExpenses = ref(0)
const totalHRExpenses = ref(0)

const http = useHTTP()
const route = useRoute()
const router = useRouter()
const alertModal = useAlertModal()

onBeforeMount(async () => {
  columnDefs.value = [
    {
      headerName: t('amount'),
      field: 'amount',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      valueFormatter: ({ data }) => {
        if (!data) return ''
        return `${formatMoney(data.amount, data.currency)}`
      }
    },
    {
      headerName: t('type'),
      field: 'type',
      valueFormatter: ({ data }) => {
        return `${t(data.type)}`
      }
    },
    {
      headerName: t('reason'),
      field: 'reason',
      valueFormatter: ({ data }) => {
        if (ctx.locale == 'en') {
          return `${t(data.en_reason)}`
        } else {
          return `${t(data.ar_reason)}`
        }
      }
    },
    {
      headerName: t('payee'),
      field: 'payee_name',
      valueFormatter: ({ data }) => {
        return data.payee_name ? `${data.payee_name}` : ''
      }
    },
    {
      headerName: t('inputDate'),
      field: 'created_at',
      valueGetter: (params) => {
        const data = params.node?.data as any
        if (!data) return ''
        if (data.created_at) {
          const created_at = new Date(data.created_at)
          return formatPostgresDate(String(created_at), 'DD/MM/YYYY hh:mm A')
        }
        return ''
      }
    },
  ]
})

function onDataLoaded(data: any) {
  totalCompanyExpenses.value = 0;
  totalHRExpenses.value = 0;
  data.forEach(element => {
    if (element.type == 'company') {
      totalCompanyExpenses.value += element.amount
    } else if (element.type == 'hr') {
      totalHRExpenses.value += element.amount
    }
  });
}

async function onSelectedTypeChanged(event: any) {
  rtable.value.payload.filter = selectedType.value;
  rtable.value.navigate(rtable.value.payload)
}
</script>
